"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { refreshProductListingAction } from "@/actions/refresh-product-listing-action";
import { useGtmProductListingTracking } from "@/hooks/use-gtm-product-listing-tracking";
import {
  ProductListingD2C,
  type ProductListingD2CProps,
} from "@/scott/ui/blocks/product-listing-d2c/product-listing-d2c";
import type { BlockInspectorProps } from "@/scott/ui/lib/inspector";
import {
  type ProductListingMicrocopies,
  type ProductListingPresentation,
  productListingServerAdapter,
} from "@/services/contentful/components/ProductListing/product-listing.server-adapter";
import type { ProductListingParams } from "@/services/product/get-product-listing";
import {
  clearProductListingPaginationState,
  getProductListingQuerySignature,
  readProductListingPaginationState,
  writeProductListingPaginationState,
} from "@/services/product/product-listing-pagination-state";
import {
  buildProductListingApiQuery,
  type ProductListingQueryState,
  readProductListingQueryState,
  splitProductListingApiQuery,
  writeProductListingQueryState,
} from "@/services/product/product-listing-query-state";
import type { UiConfiguration } from "@/types/page-block";

type ProductListingAdapterContext = {
  uiConfig: UiConfiguration;
  microcopies: ProductListingMicrocopies;
  presentation: ProductListingPresentation;
  title?: string;
  sysId?: string;
  anchorId?: string;
  routeContext?: { country?: string; language?: string };
  baseSiteId?: string;
};

export type ProductListingBlockClientProps = {
  listingId: string;
  uiProps: ProductListingD2CProps;
  productCodes: string[];
  queryContext: Pick<
    ProductListingParams,
    "categoryId" | "language" | "loadAll" | "pageSize"
  >;
  initialQueryState: ProductListingQueryState;
  initialError?: boolean;
  defaultQueryState: ProductListingQueryState;
  adapterContext: ProductListingAdapterContext;
  inspectorProps?: BlockInspectorProps;
  // Applied on every render on top of internal view state. Used by the live
  // preview wrapper to reflect editor changes without remounting the component.
  uiPropsOverrides?: Partial<ProductListingD2CProps>;
};

type ListingViewState = {
  uiProps: ProductListingD2CProps;
  productCodes: string[];
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
};

type RefreshOptions = {
  append?: boolean;
  persistPage?: boolean;
  updateUrl?: boolean;
};

function getListName(props: ProductListingD2CProps): string {
  return props.title || "Product Listing";
}

function getListId(props: ProductListingD2CProps): string | undefined {
  return props.sysId ?? props.anchorId;
}

function areQueryStatesEqual(
  left: ProductListingQueryState,
  right: ProductListingQueryState
): boolean {
  return (
    (left.filters ?? "") === (right.filters ?? "") &&
    (left.sort ?? "") === (right.sort ?? "")
  );
}

function getSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return;
  }
}

function appendListingPage(
  current: ListingViewState,
  nextUiProps: ProductListingD2CProps,
  nextProductCodes: string[]
): ListingViewState {
  const seenProductCodes = new Set(current.productCodes);
  const appendedProducts: ProductListingD2CProps["products"] = [];
  const appendedProductCodes: string[] = [];

  for (const [index, product] of nextUiProps.products.entries()) {
    const productCode = nextProductCodes[index];
    if (productCode === undefined || seenProductCodes.has(productCode)) {
      continue;
    }

    seenProductCodes.add(productCode);
    appendedProductCodes.push(productCode);
    appendedProducts.push(product);
  }

  return {
    error: false,
    loading: false,
    loadingMore: false,
    productCodes: [...current.productCodes, ...appendedProductCodes],
    uiProps: {
      ...nextUiProps,
      products: [...current.uiProps.products, ...appendedProducts],
    },
  };
}

export function ProductListingBlockClient(
  props: Readonly<ProductListingBlockClientProps>
) {
  const pathname = usePathname();
  const [view, setView] = useState<ListingViewState>({
    error: props.initialError ?? false,
    loading: false,
    loadingMore: false,
    productCodes: props.productCodes,
    uiProps: props.uiProps,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRequestId = useRef(0);
  const currentQueryState = useRef(props.initialQueryState);
  const loadMoreInFlight = useRef(false);
  const replacementInFlight = useRef(false);
  const restoredPagination = useRef(false);
  const listName = getListName(view.uiProps);
  const listId = getListId(view.uiProps);

  const trackedProducts = useGtmProductListingTracking({
    products: view.uiProps.products,
    listName,
    listId,
    containerRef,
  });

  const trackedUiProps = useMemo(
    () => ({
      ...view.uiProps,
      ...props.uiPropsOverrides,
      products: trackedProducts,
    }),
    [view.uiProps, trackedProducts, props.uiPropsOverrides]
  );

  const writeUrlState = useCallback(
    (queryState: ProductListingQueryState) => {
      const nextSearchParams = writeProductListingQueryState(
        new URLSearchParams(globalThis.location.search),
        props.listingId,
        queryState,
        props.defaultQueryState
      );
      const search = nextSearchParams.toString();

      globalThis.history.pushState(
        null,
        "",
        `${pathname}${search ? `?${search}` : ""}${globalThis.location.hash}`
      );
    },
    [pathname, props.defaultQueryState, props.listingId]
  );

  const persistPagination = useCallback(
    (queryState: ProductListingQueryState) => {
      const storage = getSessionStorage();
      if (!storage) {
        return;
      }

      writeProductListingPaginationState(
        storage,
        props.listingId,
        queryState.currentPage ?? 0,
        getProductListingQuerySignature(queryState, props.queryContext.pageSize)
      );
    },
    [props.listingId, props.queryContext.pageSize]
  );

  const refresh = useCallback(
    async (
      queryState: ProductListingQueryState,
      options: RefreshOptions = {}
    ): Promise<boolean> => {
      // A filter or sort change replaces the listing; appending a page of the
      // new query to the old products would mix both result sets.
      if (
        options.append &&
        (loadMoreInFlight.current || replacementInFlight.current)
      ) {
        return false;
      }
      loadMoreInFlight.current = options.append === true;
      replacementInFlight.current = !options.append;

      const requestId = activeRequestId.current + 1;
      activeRequestId.current = requestId;
      const previousQueryState = currentQueryState.current;
      currentQueryState.current = queryState;

      if (options.updateUrl) {
        writeUrlState(queryState);
      }

      setView((current) => ({
        ...current,
        error: false,
        loading: !options.append,
        loadingMore: options.append === true,
      }));

      const result = await refreshProductListingAction({
        ...props.queryContext,
        currentPage: queryState.currentPage,
        query: buildProductListingApiQuery(queryState.filters, queryState.sort),
        sort: queryState.sort,
      });

      if (requestId !== activeRequestId.current) {
        if (options.append) {
          loadMoreInFlight.current = false;
        }
        return false;
      }
      replacementInFlight.current = false;

      if (result.status === "error") {
        loadMoreInFlight.current = false;
        if (options.append) {
          currentQueryState.current = previousQueryState;
          setView((current) => ({ ...current, loadingMore: false }));
        } else {
          setView((current) => ({
            ...current,
            error: true,
            loading: false,
          }));
        }
        return false;
      }

      const uiProps = productListingServerAdapter(
        result.data,
        props.adapterContext.uiConfig,
        props.adapterContext.microcopies,
        props.adapterContext.presentation,
        props.adapterContext.title,
        props.adapterContext.sysId,
        props.adapterContext.anchorId,
        props.adapterContext.routeContext,
        props.adapterContext.baseSiteId
      );

      const productCodes = result.data.products
        .map((product) => product.code)
        .filter((code): code is string => code !== undefined);
      loadMoreInFlight.current = false;

      setView((current) =>
        options.append
          ? appendListingPage(current, uiProps, productCodes)
          : {
              error: false,
              loading: false,
              loadingMore: false,
              productCodes,
              uiProps,
            }
      );

      if (options.persistPage) {
        persistPagination(queryState);
      }

      return true;
    },
    [persistPagination, props.adapterContext, props.queryContext, writeUrlState]
  );

  useEffect(() => {
    if (restoredPagination.current) {
      return;
    }
    restoredPagination.current = true;

    const storage = getSessionStorage();
    if (!storage) {
      return;
    }

    if (props.queryContext.loadAll) {
      clearProductListingPaginationState(storage, props.listingId);
      return;
    }

    const initialState = { ...currentQueryState.current, currentPage: 0 };
    const storedPage = readProductListingPaginationState(
      storage,
      props.listingId,
      getProductListingQuerySignature(initialState, props.queryContext.pageSize)
    );
    const lastAvailablePage = Math.max(
      (props.uiProps.pagination?.totalPages ?? 1) - 1,
      0
    );
    const pageToRestore = Math.min(storedPage, lastAvailablePage);

    if (pageToRestore === 0) {
      persistPagination(initialState);
      return;
    }

    async function restoreLoadedPages() {
      for (let currentPage = 1; currentPage <= pageToRestore; currentPage++) {
        const restored = await refresh(
          { ...initialState, currentPage },
          { append: true, persistPage: true }
        );
        if (!restored) {
          return;
        }
      }
    }

    void restoreLoadedPages();
  }, [
    persistPagination,
    props.listingId,
    props.queryContext.loadAll,
    props.queryContext.pageSize,
    props.uiProps.pagination?.totalPages,
    refresh,
  ]);

  useEffect(() => {
    function handlePopState() {
      const queryState = readProductListingQueryState(
        new URLSearchParams(globalThis.location.search),
        props.listingId,
        props.defaultQueryState
      );

      if (areQueryStatesEqual(queryState, currentQueryState.current)) {
        return;
      }

      persistPagination(queryState);
      void refresh(queryState, { persistPage: true });
    }

    globalThis.addEventListener("popstate", handlePopState);
    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, [persistPagination, props.defaultQueryState, props.listingId, refresh]);

  function refreshFromFirstPage(overrides: Partial<ProductListingQueryState>) {
    const queryState = {
      ...currentQueryState.current,
      ...overrides,
      currentPage: 0,
    };

    persistPagination(queryState);
    void refresh(queryState, {
      persistPage: true,
      updateUrl: true,
    });
  }

  function handleFilterChange(apiQuery: string) {
    const query = splitProductListingApiQuery(
      apiQuery,
      trackedUiProps.sortOptions?.map((option) => option.code)
    );

    refreshFromFirstPage({
      filters: query.filters,
      sort: currentQueryState.current.sort ?? query.sort,
    });
  }

  return (
    <div ref={containerRef}>
      <ProductListingD2C
        {...trackedUiProps}
        error={view.error}
        inspectorProps={props.inspectorProps}
        loading={view.loading}
        loadingMore={view.loadingMore}
        onClearFilters={() =>
          refreshFromFirstPage({ filters: props.defaultQueryState.filters })
        }
        onFilterChange={handleFilterChange}
        onLoadMore={() => {
          const currentPage = currentQueryState.current.currentPage ?? 0;
          void refresh(
            { ...currentQueryState.current, currentPage: currentPage + 1 },
            { append: true, persistPage: true }
          );
        }}
        onRetry={() => void refresh(currentQueryState.current)}
        onSortChange={(sort) => refreshFromFirstPage({ sort })}
      />
    </div>
  );
}
